import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import Image from 'next/image';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import type { FormJobApplicationBody } from '@/api/form/usePostFormJobApplication';
import { usePostFormJobApplicationMutation } from '@/api/form/usePostFormJobApplication';
import type { FormUploadCVBody } from '@/api/form/usePutFormUploadCV';
import { usePutFormUploadCVMutation } from '@/api/form/usePutFormUploadCV';
import Button from '@/components/Button';
import {
  sanitizeMultiFieldInput,
  sanitizeFilename,
} from '@/lib/sanitizeMultiFieldInput';
import useGoogleRecaptcha from '@/lib/useGoogleRecaptcha';
import '@/lib/yupPhone';
import SuccessSubmittedJobApplication from './SuccessSubmittedJobApplication';
export const jobsFrom = [
  { id: 1, name: 'Facebook' },
  { id: 2, name: 'Website' },
  { id: 3, name: 'LinkedIn' },
  { id: 4, name: 'Timviecnhanh' },
  { id: 5, name: 'Ybox', value: 'ybox' },
  { id: 6, name: 'Tiktok' },
  { id: 7, name: 'Giới thiệu nội bộ' },
  { id: 8, name: 'Recruiter' },
  { id: 9, name: 'Indeed' },
  { id: 10, name: 'JobsGo' },
  { id: 11, name: 'Email' },
  { id: 12, name: 'Khác' },
];

const MAX_FILE_SIZE = 1048576 * 2; // 2MB

const schema = yup.object().shape({
  email: yup.string().required('Vui lòng nhập mail').email('Mail không hợp lệ'),
  phone_number: yup
    .string()
    .required('Vui lòng nhập số điện thoại')
    .phone('VN', 'Số diện thoại không hợp lệ'),
  name: yup.string().required('Vui lòng nhập tên'),
  cv_url: yup.string().required('Vui lòng upload CV'),
});

type Props = {
  jobTitle: string;
  jobID: number;
};

export default function JobApplicationForm({ jobTitle, jobID }: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { loadGoogleReCaptchaScript, submitCaptcha } = useGoogleRecaptcha();
  const postFormJobApplicationMutation = usePostFormJobApplicationMutation();
  const putFormUploadCVMutation = usePutFormUploadCVMutation();
  const classSelect =
    'text-neutral-50 text-body16 font-normal bg-white w-full border border-neutral-40 rounded-sm py-2 px-3 form-select focus:ring-primary-40 focus:ring-inset focus:border-white focus-visible:outline-none';
  const [successSubmit, setSuccessSubmit] = useState(false);
  const [name, setName] = useState('');
  useEffect(() => {
    loadGoogleReCaptchaScript();
  }, [loadGoogleReCaptchaScript]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
    setError,
  } = useForm<FormJobApplicationBody>({
    resolver: yupResolver(schema),
  });

  const changeUploadFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files;
    if (file && file?.length > 0) {
      setErrorMsg(''); // Reset error message

      // Validate the file first
      const rs = validateUploadFile(file[0]);

      if (rs) {
        // File is valid, proceed with upload
        onSubmitFileHandler(file[0]);
        if (errors.cv_url) {
          errors.cv_url.message = '';
        }
        setSelectedFile(file[0]);
      } else {
        // File validation failed, don't set the selected file
        setSelectedFile(null);
        // Error message is set in validateUploadFile

        // Reset file input
        event.target.value = '';
      }
    }
  };

  const onSubmitFileHandler = (file: File) => {
    const payloadFile = {
      file: file,
      dirname: 'ahamove.com',
    };
    const captcha = submitCaptcha();
    captcha.then((token) => {
      const payload = {
        ...payloadFile,
        g_recaptcha_response: token,
      };
      if (payload) {
        submitFile(payload);
      }
    });
  };

  const submitFile = (payload: FormUploadCVBody) => {
    putFormUploadCVMutation.mutate(payload, {
      onSuccess: (res) => {
        const cvUrl = res.url;
        setValue('cv_url', cvUrl, {});
      },
      onError: (err) => {
        toast.error(
          err.response?.data.url || 'Có lỗi xảy ra, vui lòng thử lại sau.'
        );
      },
    });
  };

  const validateUploadFile = (file: File) => {
    if (file) {
      // Check file size
      const fileSize = file.size;
      if (fileSize > MAX_FILE_SIZE) {
        setErrorMsg('Kích thước file vượt quá 2MB');
        return false;
      }

      // Only allow specific file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrorMsg('Chỉ chấp nhận file PDF, DOC hoặc DOCX');
        return false;
      }

      // Check file name for potential security risks
      const fileName = file.name.toLowerCase();
      const dangerousPatterns = /\.exe$|\.js$|\.jsp$|\.php$|\.asp$|\.cgi$/;
      if (dangerousPatterns.test(fileName)) {
        setErrorMsg('Loại file không được hỗ trợ');
        return false;
      }

      return true;
    }
    return false;
  };

  const submitData = (payload: FormJobApplicationBody) => {
    postFormJobApplicationMutation.mutate(payload, {
      onSuccess: () => {
        setName(getValues('name'));
        reset();
        setSuccessSubmit(true);
      },
      onError: (err) => {
        toast.error(
          err.response?.data.msg || 'Có lỗi xảy ra, vui lòng thử lại sau.'
        );
      },
    });
  };

  const onSubmitHandler = (data: FormJobApplicationBody) => {
    // Extract string fields to sanitize
    const textFieldsToSanitize = {
      name: data.name || '',
      phone_number: data.phone_number || '',
      email: data.email || '',
      relative: data.relative || '',
      other_relative: data.other_relative || '',
      referral_staff_id: data.referral_staff_id || '',
      referral_staff_name: data.referral_staff_name || '',
    };

    // Sanitize user input using improved utility with custom options
    const sanitizedFields = sanitizeMultiFieldInput(textFieldsToSanitize, {
      strictnessRatio: 0.5,
      allowHtml: false,
      encodeEntities: true,
    });

    if (!sanitizedFields.allValid) {
      // If any field fails sanitization, show errors for each invalid field
      Object.entries(sanitizedFields.results).forEach(([field, result]) => {
        if (!result.valid) {
          setError(field as keyof FormJobApplicationBody, {
            type: 'validation',
            message: result.reason || 'Nội dung không hợp lệ',
          });
        }
      });
      return;
    }

    // Prepare sanitized data, keeping all original fields but replacing sanitized ones
    const sanitizedData = {
      ...data,
      name: sanitizedFields.results.name.clean,
      phone_number: sanitizedFields.results.phone_number.clean,
      email: sanitizedFields.results.email.clean,
      relative: sanitizedFields.results.relative.clean,
      other_relative: sanitizedFields.results.other_relative.clean,
      referral_staff_id: sanitizedFields.results.referral_staff_id.clean,
      referral_staff_name: sanitizedFields.results.referral_staff_name.clean,
    };

    const captcha = submitCaptcha();
    captcha.then((token) => {
      const payload = {
        ...sanitizedData,
        g_recaptcha_response: token,
        job_title: jobTitle,
        parent_id: jobID,
      };
      submitData(payload);
    });
  };
  const classInputText =
    'w-full text-neutral-50 text-body16 font-normal placeholder:text-neutral-50 placeholder:text-base placeholder:font-normal bg-white border border-neutral-40 rounded-sm py-2 px-3 focus:ring-primary-40 focus:ring-inset focus:border-white form-input focus-visible:outline-none';
  return successSubmit ? (
    <SuccessSubmittedJobApplication name={name} job={jobTitle} />
  ) : (
    <form
      onSubmit={handleSubmit(onSubmitHandler)}
      method="post"
      className="w-full space-y-6"
    >
      <div className="space-y-1">
        <input
          className={cn(classInputText, {
            'border-red-40 focus:border-red-40': errors?.name,
            'border-neutral-40 border': !errors?.name,
          })}
          placeholder="Họ và tên"
          autoComplete="name"
          type="text"
          {...register('name')}
        />
        {errors?.name && (
          <p className="text-caption12 text-red-40 ml-2 font-normal first-letter:uppercase">
            {errors.name.message}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <input
          className={cn(classInputText, {
            'border-red-40 focus:border-red-40': errors?.phone_number,
            'border-neutral-40 border': !errors?.phone_number,
          })}
          placeholder="Số điện thoại"
          autoComplete="tel"
          type="tel"
          {...register('phone_number')}
        />
        {errors?.phone_number && (
          <p className="text-caption12 text-red-40 ml-2 font-normal first-letter:uppercase">
            {errors.phone_number.message}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <input
          className={cn(classInputText, {
            'border-red-40 focus:border-red-40': errors?.email,
            'border-neutral-40 border': !errors?.email,
          })}
          placeholder="Email"
          type="text"
          autoComplete="email"
          {...register('email')}
        />
        {errors?.email && (
          <p className="text-caption12 text-red-40 ml-2 font-normal first-letter:uppercase">
            {errors.email.message}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <select
          className={cn(classSelect, {
            'border-red-40 focus:border-red-40': errors?.relative,
            'border-neutral-40 border': !errors?.relative,
          })}
          {...register('relative')}
          onChange={(e) => {
            setValue('relative', e.target.value, {
              shouldValidate: true,
            });
          }}
        >
          <option value="" hidden>
            Bạn biết đến thông tin tuyển dụng qua kênh ....
          </option>
          {jobsFrom.map((item) => (
            <option value={item.name} key={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        {errors?.relative && (
          <p className="text-caption12 text-red-40 ml-2 font-normal first-letter:uppercase">
            {errors.relative.message}
          </p>
        )}
      </div>
      {watch('relative') === 'Nguồn khác' ? (
        <input
          className={cn(classInputText, {
            'border-red-40 focus:border-red-40': errors?.other_relative,
            'border-neutral-40 border': !errors?.other_relative,
          })}
          placeholder="Nguồn nào vậy bạn? Thêm vào đây nhé!"
          type="text"
          {...register('other_relative')}
        />
      ) : watch('relative') === 'Ahamover giới thiệu' ? (
        <div className="space-y-6">
          <input
            className={cn(classInputText, {
              'border-red-40 focus:border-red-40': errors?.referral_staff_id,
              'border-neutral-40 border': !errors?.referral_staff_id,
            })}
            placeholder="Mã nhân viên của Ahamover đó là?"
            type="text"
            {...register('referral_staff_id')}
          />
          <input
            className={cn(classInputText, {
              'border-red-40 focus:border-red-40': errors?.referral_staff_name,
              'border-neutral-40 border': !errors?.referral_staff_name,
            })}
            placeholder="Họ tên Ahamover đó là?"
            type="text"
            {...register('referral_staff_name')}
          />
        </div>
      ) : null}
      <div className="flex flex-col space-y-1">
        <p className="text-body16 text-neutral-60 font-normal">
          Tải lên CV / Hồ sơ cá nhân (tối đa 2mb)
        </p>
        <div className="flex flex-row items-center space-x-4">
          <button
            className="sapce-x-2 relative flex flex-row items-center justify-center space-x-4 overflow-hidden border border-neutral-50 px-4 py-2"
            aria-label="button"
          >
            <div className="relative h-4 w-4">
              <Image
                src="/static/icons/recruitment/UploadFile.svg"
                alt="UploadFile"
                fill
              />
            </div>
            <p className="text-neutral-90 text-body16 font-normal">
              Chọn tập tin
            </p>
            <input
              accept=".docx,.doc,.pdf"
              onChange={changeUploadFileHandler}
              type="file"
              className="absolute left-0 top-0 mt-2 scale-150 cursor-pointer opacity-0"
            />
          </button>
          {selectedFile && (
            <p className="text-caption12 text-neutral-60 ml-4 font-normal first-letter:uppercase">
              {/* Sanitize file name display to prevent XSS */}
              {sanitizeFilename(selectedFile.name)}
            </p>
          )}
        </div>
        {errorMsg !== '' && (
          <p className="text-caption12 text-red-40 ml-2 font-normal first-letter:uppercase">
            {errorMsg}
          </p>
        )}
        {errors?.cv_url && (
          <p className="text-caption12 text-red-40 ml-2 font-normal first-letter:uppercase">
            {errors.cv_url.message}
          </p>
        )}
      </div>

      <div>
        <Button
          className="desktop:w-auto mt-3 w-full "
          type="submit"
          loading={postFormJobApplicationMutation.isLoading}
          disabled={errorMsg ? true : false}
        >
          Gửi
        </Button>
      </div>
    </form>
  );
}
